import React, { useEffect, useMemo, useState } from 'react';
import { 
  AreaChart, Area, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import { 
  Users, TrendingUp, XCircle, CheckCircle, 
  LayoutDashboard, Calendar, Loader2, ArrowUpRight, 
  Wallet, Target, Zap, Clock, UserPlus
} from 'lucide-react';
import { format, subDays, startOfDay, isSameDay } from 'date-fns';
import { ru } from 'date-fns/locale';
import { getAdminAnalytics, downloadDashboardExport } from '../../services/api';
import { Download, Loader2 as Loader2Icon } from 'lucide-react';

const Dashboard = () => {
  const [period, setPeriod] = useState('week');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportLoading, setExportLoading] = useState(null);

  useEffect(() => {
    const loadAnalytics = async () => {
      try {
        setLoading(true);
        const data = await getAdminAnalytics({ period });
        setAnalytics(data);
        setError(null);
      } catch (e) {
        setError('Не удалось загрузить аналитику. Проверьте соединение с API.');
      } finally {
        setLoading(false);
      }
    };
    loadAnalytics();
  }, [period]);

  // Вычисляем показатели "За сегодня"
  const todayStats = useMemo(() => {
    if (!analytics?.daily_stats) return { opened: 0, won: 0, lost: 0, revenue: 0 };
    const today = analytics.daily_stats.find(d => isSameDay(new Date(d.date), new Date()));
    return today || { opened: 0, won: 0, lost: 0, revenue: 0 };
  }, [analytics]);

  const stats = useMemo(() => {
    if (!analytics) return { total: 0, money: 0, conversion: 0, activeCount: 0 };
    const base = analytics.stats || {};
    return {
      total: base.total_leads || 0,
      money: base.active_deals_sum || 0,
      conversion: base.conversion_rate || 0,
      activeCount: base.in_progress_count || 0
    };
  }, [analytics]);

  const handleExport = async (format) => {
    setExportLoading(format);
    try {
      await downloadDashboardExport({ format, period });
    } catch (err) {
      const msg = err.response?.data?.message ?? err.message ?? 'Не удалось скачать отчёт';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = JSON.parse(text);
          alert(json.message || msg);
        } catch (_) {
          alert(msg);
        }
      } else {
        alert(msg);
      }
    } finally {
      setExportLoading(null);
    }
  };

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-[#0f172a] p-3 sm:p-6 md:p-8 text-slate-200">
      <div className="max-w-7xl w-full mx-auto space-y-4 sm:space-y-6 md:space-y-10">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 md:gap-6">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-black tracking-tighter text-white flex items-center gap-3 italic">
              СТАТИСТИКА
            </h1>
            <div className="flex items-center gap-2 mt-2 uppercase text-[10px] font-black tracking-[0.3em] text-indigo-400">
              <div className="h-1 w-6 bg-indigo-500 rounded-full" /> Контроль KPI и динамики продаж
            </div>
          </div>
          
<div className="flex flex-wrap items-center gap-2 sm:gap-3">
  {/* Группа переключения периода */}
  <div className="flex p-1 bg-[#1e293b] rounded-2xl border border-white/5 h-[46px] items-center">
    {['week', 'month'].map((p) => (
      <button
        key={p}
        onClick={() => setPeriod(p)}
        disabled={loading}
        className={`min-w-[100px] h-full rounded-xl text-[10px] font-black tracking-widest transition-all disabled:opacity-50 flex items-center justify-center ${
          period === p 
            ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20' 
            : 'text-slate-500 hover:text-slate-300'
        }`}
      >
        {p === 'week' ? 'НЕДЕЛЯ' : 'МЕСЯЦ'}
      </button>
    ))}
  </div>

  {/* Группа экспорта */}
  <div className="flex items-center gap-2 border-l pl-3 border-white/10 h-[46px]">
    <button
      onClick={() => handleExport('excel')}
      disabled={!!exportLoading}
      className="min-w-[100px] h-full bg-emerald-600/10 text-emerald-400 border border-emerald-500/20 px-4 rounded-xl font-black text-[10px] uppercase flex items-center justify-center gap-2 hover:bg-emerald-600 hover:text-white transition-all disabled:opacity-50"
    >
      {exportLoading === 'excel' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
      <span>Excel</span>
    </button>
    
    <button
      onClick={() => handleExport('pdf')}
      disabled={!!exportLoading}
      className="min-w-[100px] h-full bg-rose-600/10 text-rose-400 border border-rose-500/20 px-4 rounded-xl font-black text-[10px] uppercase flex items-center justify-center gap-2 hover:bg-rose-600 hover:text-white transition-all disabled:opacity-50"
    >
      {exportLoading === 'pdf' ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
      <span>PDF</span>
    </button>
  </div>
</div>
        </div>

        {loading && !analytics ? (
          <div className="flex min-h-[400px] items-center justify-center rounded-[2rem] bg-[#1e293b]/20 border border-white/5">
            <Loader2 className="animate-spin text-indigo-500" size={40} />
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-rose-500/20 bg-rose-500/10 p-6 text-rose-400 font-bold">{error}</div>
        ) : (
        <>
        {/* Секция: СЕГОДНЯ (Оперативные данные) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
          <TodayMiniCard 
            label="Новых сегодня" 
            value={todayStats.opened} 
            icon={<UserPlus size={16}/>} 
            color="indigo" 
          />
          <TodayMiniCard 
            label="Успешно закрыто" 
            value={todayStats.won} 
            icon={<Zap size={16}/>} 
            color="emerald" 
          />
          <TodayMiniCard 
            label="Отказов" 
            value={todayStats.lost} 
            icon={<XCircle size={16}/>} 
            color="rose" 
          />
          <TodayMiniCard 
            label="Выручка сегодня" 
            value={`${Number(todayStats.revenue).toLocaleString()} ₸`} 
            icon={<Wallet size={16}/>} 
            color="amber" 
          />
        </div>

        {/* Основные карточки */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          <StatCard title="Всего в базе" value={stats.total} icon={<Users />} color="indigo" />
          <StatCard title="Сумма сделок" value={`${Number(stats.money).toLocaleString()} ₸`} icon={<Wallet />} color="emerald" trend="+5.4%" />
          <StatCard title="В процессе" value={stats.activeCount} icon={<TrendingUp />} color="amber" />
          <StatCard title="Конверсия" value={`${stats.conversion}%`} icon={<Target />} color="indigo" />
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
          
          {/* График активности по дням */}
          <div className="lg:col-span-2 bg-[#1e293b]/40 backdrop-blur-md p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-[2.5rem] border border-white/5 shadow-2xl">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4 sm:mb-6 md:mb-10">
              <div>
                <h3 className="text-sm sm:text-base md:text-lg font-black text-white uppercase tracking-tight">Эффективность по дням</h3>
                <p className="text-[9px] sm:text-[10px] font-bold text-slate-500 uppercase tracking-[0.2em] mt-0.5">Сравнение открытых и закрытых сделок</p>
              </div>
              <div className="flex flex-wrap gap-2 sm:gap-4 text-[8px] sm:text-[9px] font-black uppercase">
                <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-indigo-500"/> Создано</div>
                <div className="flex items-center gap-1.5"><div className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-emerald-500"/> Закрыто</div>
              </div>
            </div>
            <div className="h-[180px] sm:h-[240px] md:h-[280px] lg:h-[320px] xl:h-[350px] w-full min-h-0">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics?.daily_stats} margin={{ top: 10, right: 50, left: -44, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorWon" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ffffff05" />
                  <XAxis 
                    dataKey="date" 
                    axisLine={false} 
                    tickLine={false} 
                    tickFormatter={(str) => format(new Date(str), 'dd MMM', {locale: ru})}
                    tick={{fill: '#64748b', fontSize: 9, fontWeight: 700}} 
                  />
                  <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748b', fontSize: 9, fontWeight: 700}} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="opened" stroke="#6366f1" strokeWidth={3} fill="none" />
                  <Area type="monotone" dataKey="won" stroke="#10b981" strokeWidth={3} fill="url(#colorWon)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Рейтинг активности */}
          <div className="bg-[#1e293b]/40 backdrop-blur-md p-4 sm:p-6 md:p-8 rounded-2xl md:rounded-[2.5rem] border border-white/5 shadow-2xl flex flex-col">
            <h3 className="text-sm sm:text-base md:text-lg font-black text-white uppercase tracking-tight mb-4 sm:mb-6 md:mb-8">Лидеры периода</h3>
            <div className="space-y-3 sm:space-y-4 md:space-y-6 flex-grow">
              {analytics?.managers?.slice(0, 5).map((m, idx) => (
                <div key={idx} className="relative">
                  <div className="flex justify-between items-center mb-1 sm:mb-2">
                    <span className="text-[10px] sm:text-xs font-black text-slate-300 uppercase truncate pr-2">{m.name}</span>
                    <span className="text-[9px] sm:text-[10px] font-black text-emerald-400 shrink-0">+{m.won_leads} сделок</span>
                  </div>
                  <div className="w-full h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-indigo-500 rounded-full transition-all duration-1000"
                      style={{ width: `${(m.won_leads / (analytics?.stats?.total_won || 1)) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 sm:mt-6 md:mt-8 pt-4 sm:pt-6 border-t border-white/5">
              <div className="flex items-center gap-2 sm:gap-3 text-amber-400 bg-amber-400/5 p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-amber-400/10">
                <Zap size={18} className="shrink-0" />
                <div className="min-w-0">
                  <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest">Лучший результат</p>
                  <p className="text-xs sm:text-sm font-black text-white truncate">{analytics?.managers?.[0]?.name || '—'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Подробная таблица менеджеров */}
          <div className="lg:col-span-3 bg-[#1e293b]/40 backdrop-blur-md rounded-2xl md:rounded-[3rem] border border-white/5 shadow-2xl overflow-hidden">
            <div className="p-4 sm:p-6 md:p-8 border-b border-white/5 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <h3 className="text-sm sm:text-lg md:text-xl font-black text-white uppercase tracking-tight">Детальная статистика команды</h3>
              <Clock className="text-slate-500" size={20} />
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-white/[0.02] text-[10px] font-black uppercase tracking-widest text-slate-500">
                    <th className="px-4 sm:px-6 md:px-8 py-4 md:py-5">Менеджер</th>
                    <th className="px-4 sm:px-6 md:px-8 py-4 md:py-5 text-center">Новых</th>
                    <th className="px-4 sm:px-6 md:px-8 py-4 md:py-5 text-center">В работе</th>
                    <th className="px-4 sm:px-6 md:px-8 py-4 md:py-5 text-center">Успешно</th>
                    <th className="px-4 sm:px-6 md:px-8 py-4 md:py-5 text-center">Отказов</th>
                    <th className="px-4 sm:px-6 md:px-8 py-4 md:py-5 text-right">Выручка</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/[0.03]">
                  {analytics?.managers?.map((m, idx) => (
                    <tr key={idx} className="group hover:bg-white/[0.02] transition-colors">
                      <td className="px-8 py-5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-indigo-500/10 flex items-center justify-center text-indigo-400 font-black text-xs border border-indigo-500/20 group-hover:bg-indigo-500 group-hover:text-white transition-all">
                            {m.name.charAt(0)}
                          </div>
                          <span className="font-bold text-white text-sm">{m.name}</span>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 md:px-8 py-4 md:py-5 text-center text-slate-400 font-bold">{m.new_leads || 0}</td>
                      <td className="px-4 sm:px-6 md:px-8 py-4 md:py-5 text-center text-amber-400 font-bold">{m.processing_leads || 0}</td>
                      <td className="px-4 sm:px-6 md:px-8 py-4 md:py-5 text-center">
                        <span className="bg-emerald-500/10 text-emerald-500 px-3 py-1 rounded-full text-xs font-black">
                          {m.won_leads}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 md:px-8 py-4 md:py-5 text-center text-rose-500/50 font-bold">{m.lost_leads || 0}</td>
                      <td className="px-4 sm:px-6 md:px-8 py-4 md:py-5 text-right font-black text-white text-sm">
                        {Number(m.total_money).toLocaleString()} ₸
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
        </>
        )}
      </div>
    </div>
  );
};


const TodayMiniCard = ({ label, value, icon, color }) => {
  const colors = {
    indigo: 'text-indigo-400 bg-indigo-500/5 border-indigo-500/10',
    emerald: 'text-emerald-400 bg-emerald-500/5 border-emerald-500/10',
    rose: 'text-rose-400 bg-rose-500/5 border-rose-500/10',
    amber: 'text-amber-400 bg-amber-500/5 border-amber-400/10',
  };

  return (
    <div className={`p-2.5 sm:p-4 rounded-xl sm:rounded-2xl border ${colors[color]} flex items-center justify-between shadow-sm`}>
      <div className="min-w-0">
        <p className="text-[8px] sm:text-[9px] font-black uppercase tracking-widest opacity-60 mb-0.5 sm:mb-1">{label}</p>
        <p className="text-sm sm:text-base md:text-lg font-black leading-none truncate">{value}</p>
      </div>
      <div className="opacity-40 shrink-0 ml-1">{icon}</div>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1e293b] border border-white/10 p-4 rounded-2xl shadow-2xl backdrop-blur-md">
        <p className="text-[10px] font-black text-slate-500 uppercase mb-2">
          {label ? format(new Date(label), 'd MMMM yyyy', {locale: ru}) : ''}
        </p>
        <div className="space-y-1">
          <p className="text-xs font-bold text-indigo-400 flex justify-between gap-4">
            <span>Создано:</span> <span>{payload[0].value}</span>
          </p>
          <p className="text-xs font-bold text-emerald-400 flex justify-between gap-4">
            <span>Успешно:</span> <span>{payload[1].value}</span>
          </p>
        </div>
      </div>
    );
  }
  return null;
};

const StatCard = ({ title, value, icon, color, trend }) => {
  const colorMap = {
    indigo: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20 shadow-indigo-500/5',
    emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 shadow-emerald-500/5',
    amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20 shadow-amber-500/5',
  };

  return (
    <div className="bg-[#1e293b]/40 backdrop-blur-md p-4 sm:p-5 md:p-7 rounded-2xl sm:rounded-[2.5rem] border border-white/5 hover:border-indigo-500/30 transition-all group shadow-xl">
      <div className="flex justify-between items-start mb-3 sm:mb-4 md:mb-6">
        <div className={`p-2.5 sm:p-3 md:p-4 rounded-xl sm:rounded-2xl border ${colorMap[color]}`}>
          {React.cloneElement(icon, { size: 20, strokeWidth: 2.5 })}
        </div>
        {trend && (
          <div className="flex items-center gap-1 text-emerald-400 bg-emerald-500/10 px-2 py-1 sm:px-3 sm:py-1.5 rounded-lg sm:rounded-xl text-[9px] sm:text-[10px] font-black tracking-tighter">
            <ArrowUpRight size={12} /> {trend}
          </div>
        )}
      </div>
      <div className="min-w-0">
        <p className="text-[9px] sm:text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">{title}</p>
        <h4 className="text-lg sm:text-xl md:text-2xl font-black text-white mt-0.5 sm:mt-1 tracking-tight truncate">{value}</h4>
      </div>
    </div>
  );
};

export default Dashboard;